import * as XLSX from "xlsx";
import {
  buildPygAnaliticoStructure,
  buildExcelRows,
  calcularProyeccionPorMes,
} from "./pygAnaliticoBuild";
import {
  PYG_EMPRESA_OPTIONS,
  pygEmpresaApiParam,
} from "../../constants/pygEmpresas";
import { pygExcelI18nOpts } from "../../constants/pygLineI18n";

const TITULO_VENTA_NPGC = "1. Importe neto de la cifra de negocios.";
const TITULO_COSTE_NPGC = "4. Aprovisionamientos.";
const KEY_RESULTADO_FINAL = "RESULTADO FINAL";
const KPI_FETCH_CONCURRENCY = 6;

export const EMPRESAS_EXPORT = PYG_EMPRESA_OPTIONS.map((o) => ({
  value: o.apiValue,
  label: o.label,
}));

const appendQuery = (url, key, value) => {
  if (value === undefined || value === null || value === "") return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${key}=${encodeURIComponent(value)}`;
};

const fetchJson = async (url) => {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(res.statusText || `HTTP ${res.status}`);
  return res.json();
};

export const fetchMarcasList = async (apiUrl, empresa, year, nivel1, omitirGeneralStage) => {
  let url = `${apiUrl}/api/pyg/marcas-npgc?year=${year}&empresa=${encodeURIComponent(empresa)}`;
  if (nivel1) url = appendQuery(url, "nivel1", nivel1);
  if (omitirGeneralStage) url = appendQuery(url, "omitirGeneralStage", "1");

  const res = await fetch(url, { cache: "no-store" });
  if (res.ok) {
    const data = await res.json();
    const lista = Array.isArray(data?.marcas) ? data.marcas : [];
    return lista
      .map((m) => String(m || "").trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }
  if (res.status !== 404) throw new Error(res.statusText);

  let fallbackUrl = `${apiUrl}/api/pyg/saldos-npgc?year=${year}&empresa=${encodeURIComponent(empresa)}`;
  if (nivel1) fallbackUrl = appendQuery(fallbackUrl, "nivel1", nivel1);
  if (omitirGeneralStage) fallbackUrl = appendQuery(fallbackUrl, "omitirGeneralStage", "1");
  const rows = await fetchJson(fallbackUrl);
  const uniq = new Set();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    const marca = String(r?.Nivel2Analitica ?? r?.nivel2Analitica ?? "").trim();
    if (marca) uniq.add(marca);
  });
  return Array.from(uniq).sort((a, b) => a.localeCompare(b));
};

export const fetchPygDataset = async (apiUrl, empresa, year, nivel1, nivel2, omitirGeneralStage) => {
  const base = `${apiUrl}/api/pyg`;
  const q = (path) => {
    let url = `${base}/${path}?year=${year}&empresa=${encodeURIComponent(empresa)}`;
    if (nivel1) url = appendQuery(url, "nivel1", nivel1);
    if (nivel2) url = appendQuery(url, "nivel2", nivel2);
    if (omitirGeneralStage) url = appendQuery(url, "omitirGeneralStage", "1");
    return url;
  };

  const [rawData, proyeccionVentas, proyeccionCompras, pedidosVenta, pedidosCompra, facturasBorradorVenta] =
    await Promise.all([
      fetchJson(q("saldos-npgc")),
      fetchJson(q("proyeccion_ventas")).catch(() => []),
      fetchJson(q("proyeccion_compras")).catch(() => []),
      fetchJson(q("pedidos_venta")).catch(() => []),
      fetchJson(q("pedidos_compra")).catch(() => []),
      fetchJson(q("facturas_venta_borrador")).catch(() => []),
    ]);

  const series = {
    resultadoProyeccionVentas: calcularProyeccionPorMes(
      Array.isArray(proyeccionVentas) ? proyeccionVentas : [],
      year,
    ),
    resultadoProyeccionCompras: calcularProyeccionPorMes(
      Array.isArray(proyeccionCompras) ? proyeccionCompras : [],
      year,
    ),
    resultadoPedidosVenta: calcularProyeccionPorMes(
      Array.isArray(pedidosVenta) ? pedidosVenta : [],
      year,
    ),
    resultadoPedidosCompra: calcularProyeccionPorMes(
      Array.isArray(pedidosCompra) ? pedidosCompra : [],
      year,
    ),
    resultadoFacturasBorradorVenta: calcularProyeccionPorMes(
      Array.isArray(facturasBorradorVenta) ? facturasBorradorVenta : [],
      year,
    ),
  };

  return buildPygAnaliticoStructure(
    Array.isArray(rawData) ? rawData : [],
    series,
  );
};

/** KPI por marca: mismo pipeline que la tabla PyG embebida (fetchPygDataset). */
export const fetchMarcasKpi = async (
  apiUrl,
  empresa,
  year,
  nivel1,
  marcas,
  onProgress,
  omitirGeneralStage,
  t,
) => {
  const emp = pygEmpresaApiParam(empresa);
  const byMarca = {};

  const buildKpiMarca = async (marca) => {
    onProgress?.(
      t
        ? t("pygAnalitico.exportCalculating", { marca })
        : `Calculando ${marca}…`,
    );
    const { groupMap } = await fetchPygDataset(
      apiUrl,
      emp,
      year,
      nivel1,
      marca,
      omitirGeneralStage,
    );

    byMarca[marca] = {
      venta: groupMap[TITULO_VENTA_NPGC]?.total ?? 0,
      coste: groupMap[TITULO_COSTE_NPGC]?.total ?? 0,
      proyeccionVentas: groupMap["PROYECCIÓN VENTAS"]?.total ?? 0,
      proyeccionCompras: groupMap["PROYECCIÓN COMPRAS"]?.total ?? 0,
      pedidosVenta: groupMap["PEDIDOS VENTA"]?.total ?? 0,
      pedidosCompra: groupMap["PEDIDOS COMPRA"]?.total ?? 0,
      facturasBorradorVenta: groupMap["FACTURAS BORRADOR VENTA"]?.total ?? 0,
      resultado: groupMap[KEY_RESULTADO_FINAL]?.total ?? 0,
    };
  };

  for (let i = 0; i < marcas.length; i += KPI_FETCH_CONCURRENCY) {
    const batch = marcas.slice(i, i + KPI_FETCH_CONCURRENCY);
    await Promise.all(batch.map((marca) => buildKpiMarca(marca)));
  }

  return byMarca;
};

export const sanitizeSheetName = (name, usedNames) => {
  let base = String(name || "Hoja")
    .replace(/[\\/?*[\]:]/g, "")
    .trim();
  if (!base) base = "Hoja";
  base = base.slice(0, 31);

  let candidate = base;
  let n = 1;
  while (usedNames.has(candidate)) {
    const suffix = `_${n}`;
    candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    n += 1;
  }
  usedNames.add(candidate);
  return candidate;
};

export const downloadMultiSheetWorkbook = (sheets, filename) => {
  const wb = XLSX.utils.book_new();
  const usedNames = new Set();

  sheets.forEach(({ sheetName, rows, headers }) => {
    const name = sanitizeSheetName(sheetName, usedNames);
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
    XLSX.utils.book_append_sheet(wb, ws, name);
  });

  XLSX.writeFile(wb, filename);
};

export const exportPygPorMarcas = async ({
  apiUrl,
  empresa,
  year,
  nivel1,
  omitirGeneralStage,
  marcas: marcasInput,
  onProgress,
  t,
}) => {
  const emp = pygEmpresaApiParam(empresa);
  const marcas =
    marcasInput && marcasInput.length > 0
      ? marcasInput
      : await fetchMarcasList(apiUrl, emp, year, nivel1, omitirGeneralStage);

  if (!marcas.length) {
    throw new Error(
      t ? t("pygAnalitico.noBrandsExport") : "No hay marcas para exportar.",
    );
  }

  const sheets = [];
  for (let i = 0; i < marcas.length; i += 1) {
    const marca = marcas[i];
    onProgress?.(
      t
        ? t("pygAnalitico.exportBrandProgress", {
            current: i + 1,
            total: marcas.length,
            marca,
          })
        : `Marca ${i + 1}/${marcas.length}: ${marca}`,
    );
    const { groupOrder, groupMap } = await fetchPygDataset(
      apiUrl,
      emp,
      year,
      nivel1,
      marca,
      omitirGeneralStage,
    );
    const { headers, rows } = buildExcelRows(
      groupOrder,
      groupMap,
      undefined,
      pygExcelI18nOpts(t),
    );
    sheets.push({ sheetName: marca, headers, rows });
  }

  const n1 = nivel1 ? `-${nivel1}` : "";
  downloadMultiSheetWorkbook(
    sheets,
    `pyg-analitico-${emp}-marcas${n1}-${year}.xlsx`,
  );
  return marcas.length;
};

export const exportPygPorEmpresas = async ({
  apiUrl,
  year,
  nivel1,
  nivel2,
  empresas = EMPRESAS_EXPORT.map((e) => e.value),
  onProgress,
  t,
}) => {
  const sheets = [];
  for (let i = 0; i < empresas.length; i += 1) {
    const empresa = empresas[i];
    const label =
      EMPRESAS_EXPORT.find((e) => e.value === empresa)?.label || empresa;
    onProgress?.(
      t
        ? t("pygAnalitico.exportCompanyProgress", {
            current: i + 1,
            total: empresas.length,
            label,
          })
        : `Empresa ${i + 1}/${empresas.length}: ${label}`,
    );
    const { groupOrder, groupMap } = await fetchPygDataset(
      apiUrl,
      empresa,
      year,
      nivel1,
      nivel2,
    );
    const { headers, rows } = buildExcelRows(
      groupOrder,
      groupMap,
      undefined,
      pygExcelI18nOpts(t),
    );
    sheets.push({ sheetName: label, headers, rows });
  }

  const n1 = nivel1 ? `-${nivel1}` : "";
  const n2 = nivel2 ? `-${nivel2}` : "";
  downloadMultiSheetWorkbook(
    sheets,
    `pyg-analitico-empresas${n1}${n2}-${year}.xlsx`,
  );
  return empresas.length;
};
